import { ContributionOption } from "../../../../types";
import { Options } from "../../../../types";
import { EmployeePledgeGiftInvestmentViewModel } from "../../../../viewModels/employeePledgeGiftInvestmentViewModel";
import { EMPTY_GUID } from "../../../../utility/constants";
import {
  GIFT_INVESTMENT_OPTION_OTHER_ID,
  PAYROLL_REDUCTION_ID,
  UNDESIGNATED_OPTION_ID,
} from "../constants";

export const UNDESIGNATED_LABEL =
  "Undesignated - Will go to the Greatest Impact fund";

const DAILY_CONTRIBUTION_MULTIPLIERS: Record<string, number> = {
  [ContributionOption.$2Day]: 2,
  [ContributionOption.$3Day]: 3,
  [ContributionOption.$4Day]: 4,
  [ContributionOption.$5Day]: 5,
  [ContributionOption.$10Day]: 10,
};

const roundCurrency = (value: number) => Number(value.toFixed(2));

interface BuildGiftInvestmentAmountsArgs {
  selectedIds: string[] | null;
  existingAmounts: EmployeePledgeGiftInvestmentViewModel[] | null;
  giftOptions: Options[];
  employeeId: string | null | undefined;
  pledgeTotal: number;
}

interface UpdateGiftInvestmentAmountArgs {
  allocations: EmployeePledgeGiftInvestmentViewModel[];
  updatedValue: number;
  giftInvestmentId: string | null;
  agencyId: string | null;
  pledgeTotal: number;
  employeeId: string | null | undefined;
}

export const getContributionAmount = (
  option: string,
  paymentOptionId: string | null
) => {
  if (option === ContributionOption.Other) {
    return paymentOptionId === PAYROLL_REDUCTION_ID ? 24 : null;
  }

  return 365 * (DAILY_CONTRIBUTION_MULTIPLIERS[option] ?? 1);
};

const getDistribution = (total: number, count: number) => {
  let amount = roundCurrency(total / count);
  let remainder = roundCurrency(total - amount * count);

  if (remainder < 0) {
    amount = roundCurrency(amount - 0.01);
    remainder = roundCurrency(total - amount * count);
  }

  return { amount, remainder };
};

export const buildGiftInvestmentAmounts = ({
  selectedIds,
  existingAmounts,
  giftOptions,
  employeeId,
  pledgeTotal,
}: BuildGiftInvestmentAmountsArgs) => {
  const otherAgencies = (existingAmounts ?? []).filter(
    (item) =>
      item.giftInvestmentId === GIFT_INVESTMENT_OPTION_OTHER_ID &&
      item.isDropdown
  );
  const investmentCount =
    (selectedIds?.length ?? 1) +
    Math.max(otherAgencies.length - 1, 0);
  const { amount, remainder } = getDistribution(
    roundCurrency(pledgeTotal ?? 0),
    investmentCount
  );
  const createInvestment = (
    name: string | null,
    giftInvestmentId: string,
    agencyId: string | null,
    pledgeAmount: number,
    isDropdown?: boolean
  ) =>
    new EmployeePledgeGiftInvestmentViewModel(
      EMPTY_GUID,
      name,
      giftInvestmentId,
      employeeId,
      agencyId,
      pledgeAmount,
      null,
      isDropdown
    );

  const allocations = [
    createInvestment(
      UNDESIGNATED_LABEL,
      UNDESIGNATED_OPTION_ID,
      null,
      remainder
    ),
    ...(selectedIds ?? []).map((id: string) =>
      createInvestment(
        giftOptions.find((option) => option.value === id)?.label ?? null,
        id,
        null,
        amount
      )
    ),
  ];

  if (selectedIds?.includes(GIFT_INVESTMENT_OPTION_OTHER_ID)) {
    allocations.push(
      ...otherAgencies.map((item) =>
        createInvestment(
          item.giftInvestment ?? null,
          GIFT_INVESTMENT_OPTION_OTHER_ID,
          item.agencyId ?? null,
          amount,
          true
        )
      )
    );
  }

  return allocations;
};

export const updateGiftInvestmentAmount = ({
  allocations,
  updatedValue,
  giftInvestmentId,
  agencyId,
  pledgeTotal,
  employeeId,
}: UpdateGiftInvestmentAmountArgs) => {
  const matches = (item: EmployeePledgeGiftInvestmentViewModel) =>
    giftInvestmentId
      ? item.giftInvestmentId === giftInvestmentId
      : item.agencyId === agencyId;
  let updated = allocations.map((item) =>
    matches(item)
      ? { ...item, pledgeAmount: Number(updatedValue) }
      : item
  );
  const designatedTotal = updated
    .filter(
      (item) =>
        item.giftInvestmentId !== UNDESIGNATED_OPTION_ID &&
        !(
          item.giftInvestmentId === GIFT_INVESTMENT_OPTION_OTHER_ID &&
          item.agencyId === null
        )
    )
    .reduce(
      (total, item) => total + (item.pledgeAmount ?? 0),
      0
    );
  const remainder = roundCurrency(pledgeTotal - designatedTotal);
  const undesignated = updated.find(
    (item) => item.giftInvestmentId === UNDESIGNATED_OPTION_ID
  );

  if (!undesignated) {
    updated.push(
      new EmployeePledgeGiftInvestmentViewModel(
        null,
        UNDESIGNATED_LABEL,
        UNDESIGNATED_OPTION_ID,
        employeeId,
        null,
        remainder,
        null,
        undefined
      )
    );
  } else {
    updated = updated.map((item) =>
      item.giftInvestmentId === UNDESIGNATED_OPTION_ID
        ? { ...item, pledgeAmount: remainder }
        : item
    );
  }

  return updated;
};
